import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { ListItem } from "@/components/campaign/ListItem";
import { CheckCard } from "@/components/generic/CheckCard";
import { Select, SelectItem } from "@/components/generic/Select";
import { TagField } from "@/components/generic/TagField";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { useTechnologyGroup } from "@/services/technologies.service";
import { Technology } from "@/types/technologies";
import { ListOperator } from "@/utils/operators";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  shouldInclude: z.boolean(),
  operator: z.custom<ListOperator>(),
  values: z.array(z.string()),
});

export function TechnologyFilter({
  group: {
    title,
    description,
    checkboxLabel,
    technologyDescription,
    category,
    filters,
  },
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<
  "hasTechnology" | "technology",
  {
    category: string;
    checkboxLabel: string;
    technologyDescription: string;
  }
>) {
  const hasTechFilter = queryFilters.hasTechnology;
  const technologyFilter = queryFilters.technology;

  const {
    data: technologiesData,
    isFetching,
    isLoading,
  } = useTechnologyGroup(category);
  const technologies = useMemo(() => {
    if (category === "other") {
      // Exclude technologies that are part of other categories
      return (
        technologiesData
          ?.filter((tech) => {
            return ![
              "live chat",
              "crm",
              "ecommerce",
              "cms",
              "digital marketing",
            ].includes(tech.category);
          })
          .sort((a, b) => a.technology.localeCompare(b.technology))
          // Deduplicate technologies
          .reduce<Technology[]>((acc, current) => {
            if (!acc.find((tech) => tech.technology === current.technology)) {
              acc.push(current);
            }
            return acc;
          }, []) ?? []
      );
    }
    return technologiesData ?? [];
  }, [technologiesData, category]);

  const { control, handleSubmit, watch, setValue, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      shouldInclude:
        filters.hasTechnology !== null
          ? hasTechFilter?.values?.includes(category) ||
            (technologyFilter?.values?.length ?? 0) > 0
          : true,
      operator: (technologyFilter?.operator || "ListContains") as ListOperator,
      values: technologyFilter?.values || [],
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const { shouldInclude, values, operator } = watch();

  const submit = handleSubmit((data) => {
    if (data.shouldInclude) {
      if (data.operator === "ListContains") {
        update({
          technology:
            data.values.length > 0
              ? {
                  operator: data.operator,
                  values: data.values,
                }
              : undefined,
          hasTechnology:
            data.values.length > 0
              ? undefined
              : {
                  operator: "ListContains",
                  values: [category],
                },
        });
      } else {
        update({
          hasTechnology: filters.hasTechnology
            ? {
                operator: "ListContains",
                values: [category],
              }
            : undefined,
          technology:
            data.values.length > 0
              ? {
                  operator: data.operator,
                  values: data.values,
                }
              : undefined,
        });
      }
    } else {
      clear();
    }
  });

  function clear() {
    update({
      hasTechnology: undefined,
      technology: undefined,
    });
  }

  if (isFetching || isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center py-48">
        <Loader2 className="text-theme-on-background-variant size-6 animate-spin" />
      </div>
    );
  }

  return (
    <Form
      className="mx-auto flex h-full w-auto flex-col items-center justify-between"
      id={formId}
      onSubmit={submit}
    >
      <div className="flex w-full flex-col items-center gap-y-9">
        <SegmentHeader title={title} description={description} />
        {filters.hasTechnology !== null && (
          <CheckCard
            control={control}
            name="shouldInclude"
            label={checkboxLabel}
            data-testid="tech-checkbox"
          />
        )}
        <AnimatePresence>
          {(shouldInclude || filters.hasTechnology === null) && (
            <motion.div
              key={`${category}-tech-filter-options`}
              className="flex w-full flex-col gap-y-6"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.15, ease: "easeInOut" }}
            >
              <SegmentHeader
                className="gap-y-0"
                title=""
                description={technologyDescription}
              />
              <div className="flex gap-x-2">
                <Controller
                  control={control}
                  name="operator"
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      className="w-full"
                      placeholder="Operator"
                      aria-label="Operator"
                      selectedKey={field.value}
                      onSelectionChange={field.onChange}
                      items={[
                        { id: "ListContains", label: "Skal være" },
                        { id: "ListDoesNotContain", label: "Må ikke være" },
                      ]}
                      errorMessage={error?.message}
                    >
                      {(item) => (
                        <SelectItem id={item.id}>{item.label}</SelectItem>
                      )}
                    </Select>
                  )}
                />
                <Controller
                  control={control}
                  name="values"
                  render={({ field, fieldState: { error } }) => (
                    <TagField
                      className="w-full"
                      placeholder="Teknologi"
                      aria-label="Teknologi"
                      selectedOptions={field.value.map((value) => ({
                        label: value,
                        value,
                      }))}
                      setSelectedOptions={(value) => {
                        if (Array.isArray(value))
                          return field.onChange(
                            value.map((item) => item.value),
                          );
                      }}
                      options={technologies.map((technology) => ({
                        label: technology.technology,
                        value: technology.technology,
                      }))}
                      errorMessage={error?.message}
                    />
                  )}
                />
              </div>
              <ul className="flex max-h-[220px] flex-col gap-y-2 overflow-y-auto">
                {values.map((value, index) => (
                  <ListItem
                    key={`${category}-${value}`}
                    label={`${
                      operator === "ListContains" ? "Skal være" : "Må ikke være"
                    } ${value}`}
                    onRemove={() => {
                      const newValues = [...values];
                      newValues.splice(index, 1);
                      setValue("values", newValues);
                    }}
                  />
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Form>
  );
}
